import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageLayout } from '../components/page-layout';
import { useAuth0 } from '@auth0/auth0-react';
import { uploadActivity, updateActivityName, deleteActivity, getCompletedWorkouts } from 'src/services/workouts.service';
import { StrongifierCompletedWorkoutV2 } from 'src/models/strongifier-completed-workout-v2';
import { Upload, CheckCircle, AlertCircle, Pencil, Check, X, Trash2 } from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import { getActivityVisual } from '../components/home/activity-format';

const SOURCE_LABELS: Record<string, string> = {
  fit: 'FIT upload',
  manual: 'Manual entry',
  strava_migration: 'Strava migration',
};

/** "VirtualRide" -> "Virtual Ride", "TrailRun" -> "Trail Run" */
const displayType = (activityType: string): string =>
  (activityType || '').replace(/([a-z])([A-Z])/g, '$1 $2');

/** "Aug 7, 2026 · 8:15 AM" */
const formatDateTime = (d: Date): string =>
  isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) +
      ' · ' +
      d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

// Convert duration in minutes to H:MM:SS
const formatDuration = (mins: number): string => {
  const totalSec = Math.round(mins * 60);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${h}:${pad(m)}:${pad(s)}`;
};

const formatDistance = (km: number): string => `${km.toFixed(1)} km`;

const getWorkoutName = (w: StrongifierCompletedWorkoutV2) => w.workout_name ?? '—';

const getWorkoutDate = (w: StrongifierCompletedWorkoutV2) =>
  new Date(w.local_activity_datetime ?? w.activity_datetime);

/**
 * The Workouts tab, backed by the v2 engine's table. Uploads go through the
 * shared upload endpoint (which dual-writes v1+v2); renames and deletes go
 * through the shared endpoints, which keep the v2 table in sync.
 */
export const WorkoutsPage: React.FC = () => {
  const { getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();
  const [workouts, setWorkouts] = useState<StrongifierCompletedWorkoutV2[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadingName, setUploadingName] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<StrongifierCompletedWorkoutV2 | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const fetchWorkouts = useCallback(async () => {
    const token = await getAccessTokenSilently();
    const resp = await getCompletedWorkouts(token);
    if (resp.error) setError(resp.error.message);
    else setWorkouts(resp.data);
  }, [getAccessTokenSilently]);

  useEffect(() => {
    (async () => {
      try {
        await fetchWorkouts();
      } catch (e: any) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [fetchWorkouts]);

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) return;

    const file = acceptedFiles[0];
    setUploading(true);
    setUploadingName(file.name);
    setError(null);
    setUploadSuccess(null);

    try {
      const token = await getAccessTokenSilently();
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const activityName = `activity-${timestamp}.fit`;

      const resp = await uploadActivity(token, file, activityName, timezone);

      if (resp.error) {
        setError(resp.error.message);
      } else {
        setUploadSuccess(`${file.name} processed`);
        // Re-fetch so we show the analyzed v2 record, not the v1 response
        await fetchWorkouts();
      }
    } catch (e: any) {
      setError(e.message);
    } finally {
      setUploading(false);
      setUploadingName(null);
    }
  }, [getAccessTokenSilently, fetchWorkouts]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/octet-stream': ['.fit']
    },
    maxFiles: 1,
    disabled: uploading
  });

  useEffect(() => {
    if (uploadSuccess) {
      const timer = setTimeout(() => setUploadSuccess(null), 3000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [uploadSuccess]);

  const startEdit = (w: StrongifierCompletedWorkoutV2) => {
    if (saving) return;
    setEditError(null);
    setEditingId(w.id);
    setEditingValue(w.workout_name ?? '');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingValue('');
    setEditError(null);
  };

  const commitEdit = async () => {
    if (!editingId) return;
    const trimmed = editingValue.trim();
    if (!trimmed) {
      setEditError('Name cannot be empty.');
      return;
    }
    setSaving(true);
    setEditError(null);
    try {
      const token = await getAccessTokenSilently();
      const resp = await updateActivityName(token, editingId, trimmed);
      if (resp.error) {
        setEditError(resp.error.message);
      } else {
        // Rename syncs the v2 row server-side; re-fetch for re-analyzed flags
        await fetchWorkouts();
        cancelEdit();
      }
    } catch (e: any) {
      setEditError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleNameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitEdit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancelEdit();
    }
  };

  const openDeleteModal = (w: StrongifierCompletedWorkoutV2) => {
    setDeleteTarget(w);
    setDeleteError(null);
  };
  const closeDeleteModal = () => {
    setDeleteTarget(null);
    setDeleteError(null);
    setDeleting(false);
  };
  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      const token = await getAccessTokenSilently();
      const resp = await deleteActivity(token, deleteTarget.id);
      if (!resp.ok) {
        setDeleteError(resp.error?.message || 'Delete failed');
        setDeleting(false);
        return;
      }
      setWorkouts(prev => prev.filter(a => a.id !== deleteTarget.id));
      closeDeleteModal();
    } catch (e: any) {
      setDeleteError(e.message);
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <PageLayout>
        <div className="content-layout">
          <div className="content__body">
            <p className="text-center text-[#9aa5b8]">Loading workouts...</p>
          </div>
        </div>
      </PageLayout>
    );
  }
  if (error && workouts.length === 0) {
    return (
      <PageLayout>
        <div className="content-layout">
          <div className="content__body">
            <p className="text-center text-red-400">Error: {error}</p>
          </div>
        </div>
      </PageLayout>
    );
  }
  return (
    <PageLayout>
      <div className="content-layout">
        <div className="content__body p-4">
          <div className="max-w-[1180px] mx-auto">
            <div className="mb-[22px]">
              <h1 className="font-['Chakra_Petch'] text-[25px] font-semibold m-0 mb-1.5 tracking-[.4px] text-[#e8ecf4]">
                Workouts
              </h1>
              <div className="text-[14px] text-[#9aa5b8]">
                Your complete workout history. Upload a .FIT file to add one, or
                click any workout to see its full analysis.
              </div>
            </div>

            {/* Upload strip: idle / processing / done */}
            {uploading ? (
              <div className="border-[1.5px] border-dashed border-[#33405c] rounded-[10px] px-[18px] py-5 flex items-center justify-center gap-3 mb-5">
                <div className="w-[9px] h-[9px] rounded-full bg-[#e0764a] animate-pulse" />
                <span className="text-[14px] font-semibold text-[#dfe5f0]">
                  Processing {uploadingName ?? 'activity.fit'}…
                </span>
                <span className="text-[11.5px] text-[#6b7688]">Extracting laps, HR and pace data</span>
              </div>
            ) : uploadSuccess ? (
              <div className="border-[1.5px] border-[#275445] rounded-[10px] px-[18px] py-5 flex items-center justify-center gap-3 mb-5 bg-[rgba(62,201,167,.05)]">
                <CheckCircle className="w-4 h-4 text-[#3ec9a7]" />
                <span className="text-[14px] font-semibold text-[#dfe5f0]">{uploadSuccess}</span>
              </div>
            ) : (
              <div
                {...getRootProps()}
                className={`border-[1.5px] border-dashed rounded-[10px] px-[18px] py-5 flex items-center justify-center gap-3 mb-5 cursor-pointer transition-colors ${
                  isDragActive ? 'border-[#5661f0] bg-[rgba(86,97,240,.08)]' : 'border-[#33405c] hover:border-[#46527a]'
                }`}
              >
                <input {...getInputProps()} />
                <Upload className="w-[18px] h-[18px] text-[#8fa3ff]" />
                <span className="text-[14px] font-semibold text-[#dfe5f0]">
                  Drop a .fit file here <span className="text-[#8fa3ff]">or browse</span>
                </span>
                <span className="text-[11.5px] text-[#6b7688]">Garmin · Wahoo · COROS</span>
              </div>
            )}

            {error && (
              <div className="mb-4 flex items-center justify-center gap-2 text-red-400 text-[13.5px]">
                <AlertCircle className="w-4 h-4" />
                <span>Error: {error}</span>
              </div>
            )}

            {/* Delete Confirmation Modal */}
            {deleteTarget && (
              <div className="fixed inset-0 z-50">
                <div
                  className="fixed inset-0 bg-[#050608]/60 transition-opacity"
                  onClick={deleting ? undefined : closeDeleteModal}
                />
                <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                  <div
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="delete-dialog-title"
                    className="relative transform overflow-hidden rounded-[10px] bg-[#1a2130] text-left shadow-xl outline -outline-offset-1 outline-[#242e42] transition-all sm:my-8 sm:w-full sm:max-w-2xl"
                  >
                    <div className="bg-[#1a2130] px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                      <div className="sm:flex sm:items-start">
                        <div className="mx-auto flex size-12 shrink-0 items-center justify-center rounded-full bg-red-500/10 sm:mx-0 sm:size-10">
                          <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            aria-hidden="true"
                            className="size-6 text-red-400"
                          >
                            <path
                              d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </div>
                        <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                          <h3 id="delete-dialog-title" className="text-3xl font-semibold text-[#e8ecf4]">
                            Delete workout?
                          </h3>
                          <div className="mt-2">
                            <p className="text-2xl text-[#aab4c8]">
                              Are you sure you want to delete:
                              <br />
                              <span className="font-medium text-[#e8ecf4]">
                                {getWorkoutName(deleteTarget)}
                              </span>
                              <br />
                              on {getWorkoutDate(deleteTarget).toLocaleString()}?
                            </p>
                            {deleteError && (
                              <p className="mt-2 text-xl text-red-400">{deleteError}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="bg-[#1e2636] px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                      <button
                        onClick={confirmDelete}
                        disabled={deleting}
                        className="inline-flex w-full justify-center rounded-md bg-red-500 px-3 py-2 text-3xl font-semibold text-white hover:bg-red-400 disabled:opacity-50 sm:ml-3 sm:w-auto"
                      >
                        {deleting ? (
                          <span className="inline-flex items-center">
                            <svg className="mr-2 h-4 w-4 animate-spin" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
                            </svg>
                            Deleting...
                          </span>
                        ) : (
                          "Delete"
                        )}
                      </button>
                      <button
                        onClick={closeDeleteModal}
                        disabled={deleting}
                        className="mt-3 inline-flex w-full justify-center rounded-md bg-white/10 px-3 py-2 text-3xl font-semibold text-white inset-ring inset-ring-white/5 hover:bg-white/20 disabled:opacity-50 sm:mt-0 sm:w-auto"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <section className="bg-[#1a2130] border border-[#242e42] rounded-[10px] overflow-hidden">
              <div
                className="grid gap-2.5 items-center px-5 py-3 border-b border-[#232c3e]"
                style={{ gridTemplateColumns: 'minmax(140px,1.6fr) 90px 110px 150px 72px 72px 26px' }}
              >
                {['Name', 'Type', 'Source', 'Date & time', 'Duration', 'Distance', ''].map((label, i) => (
                  <div
                    key={label || 'actions'}
                    className={`text-[10.5px] tracking-[1.1px] uppercase text-[#6b7688] ${i >= 4 && i <= 5 ? 'text-right' : ''}`}
                  >
                    {label}
                  </div>
                ))}
              </div>
              {workouts.length === 0 && (
                <div className="px-5 py-6 text-center text-[#9aa5b8] text-[13.5px]">
                  No workouts yet — upload a .FIT file to get started.
                </div>
              )}
              {workouts.map((w) => {
                const isEditing = editingId === w.id;
                const isDeletingThis = deleteTarget?.id === w.id && deleting;
                const visual = getActivityVisual(w.activity_type);
                return (
                  <div
                    key={w.id}
                    onClick={isEditing ? undefined : () => navigate(`/workouts/${w.id}`)}
                    className="grid gap-2.5 items-center px-5 py-[11px] border-t border-[#1f2839] cursor-pointer hover:bg-[#1e2636]"
                    style={{ gridTemplateColumns: 'minmax(140px,1.6fr) 90px 110px 150px 72px 72px 26px' }}
                  >
                    <div className="flex items-center gap-[11px] min-w-0" onClick={isEditing ? (e) => e.stopPropagation() : undefined}>
                      <div
                        className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ background: visual.ringColor }}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={visual.color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d={visual.iconPath}></path>
                        </svg>
                      </div>
                      {isEditing ? (
                        <div className="flex flex-col gap-1 min-w-0 flex-1">
                          <div className="flex items-center gap-1.5">
                            <input
                              className="bg-[#10151f] text-[#e8ecf4] text-[13.5px] px-2 py-1 rounded border border-[#2e3850] focus:outline-none focus:ring-2 focus:ring-[#5661f0] min-w-0 flex-1"
                              value={editingValue}
                              onChange={(e) => setEditingValue(e.target.value)}
                              onKeyDown={handleNameKeyDown}
                              autoFocus
                              disabled={saving}
                              placeholder="Workout name"
                            />
                            <button onClick={commitEdit} disabled={saving} className="text-green-400 hover:text-green-300 disabled:opacity-50" title="Save">
                              <Check className="w-4 h-4" />
                            </button>
                            <button onClick={cancelEdit} disabled={saving} className="text-red-400 hover:text-red-300 disabled:opacity-50" title="Cancel">
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                          {editError && <div className="text-xs text-red-400">{editError}</div>}
                        </div>
                      ) : (
                        <>
                          <span className="text-[13.5px] font-semibold text-[#dfe5f0] overflow-hidden text-ellipsis whitespace-nowrap">
                            {getWorkoutName(w)}
                          </span>
                          <button
                            onClick={(e) => { e.stopPropagation(); startEdit(w); }}
                            className="text-[#46527a] hover:text-[#e8ecf4] flex-shrink-0"
                            title="Edit name"
                          >
                            <Pencil className="w-3 h-3" />
                          </button>
                        </>
                      )}
                    </div>
                    <div className="text-[13px] text-[#9aa5b8]">{displayType(w.activity_type)}</div>
                    <div className="text-[12.5px] text-[#9aa5b8]">{SOURCE_LABELS[w.source] ?? w.source}</div>
                    <div className="text-[12.5px] text-[#9aa5b8]">{formatDateTime(getWorkoutDate(w))}</div>
                    <div className="text-[13px] font-semibold text-[#dfe5f0] text-right">{formatDuration(w.duration_minutes)}</div>
                    <div className="text-[13px] font-semibold text-[#dfe5f0] text-right">{formatDistance(w.distance_km)}</div>
                    <button
                      onClick={(e) => { e.stopPropagation(); openDeleteModal(w); }}
                      disabled={isDeletingThis || saving}
                      title="Delete workout"
                      className="flex items-center justify-center text-[#46527a] hover:text-[#e0655c] disabled:opacity-50"
                    >
                      <Trash2 className="w-[15px] h-[15px]" />
                    </button>
                  </div>
                );
              })}
            </section>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default WorkoutsPage;
